import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';

interface UserItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  isActive: boolean;
}

interface AdminPayment {
  id: number;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  notes: string | null;
  isPaid: boolean;
  expiryDate: string | null;
  transactionId: string | null;
  paymentStatus: string;
}

interface InstructorSubscription {
  instructorId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  subscriptionStatus: string; // "Active" | "Expired" | "None"
  subscriptionType: string; // "Trial" | "Monthly" | "Annual" | "None"
  expiryDate: string | null;
  hasWhatsAppAddon: boolean;
  lastPaymentAmount: number | null;
  lastPaymentDate: string | null;
  lastPaymentMethod: string | null;
  paymentHistory?: AdminPayment[];
}

interface DashboardStats {
  totalUsers: number;
  totalInstructors: number;
  totalStudents: number;
  activeUsers: number;
  inactiveUsers: number;
}

const AdminDashboard: React.FC = () => {
  const confirm = useConfirm();
  const showToast = useToast();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<InstructorSubscription[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Navigation & filter states
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  
  // Pending bank transfers states
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [approvingPaymentId, setApprovingPaymentId] = useState<number | null>(null);

  // Edit subscription states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<InstructorSubscription | null>(null);
  const [subType, setSubType] = useState<string>('None');
  const [subExpiryDate, setSubExpiryDate] = useState<string>('');
  const [hasWhatsApp, setHasWhatsApp] = useState<boolean>(false);
  const [subNotes, setSubNotes] = useState<string>('');
  const [savingSub, setSavingSub] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [usersRes, statsRes, pendingPaymentsRes, subsRes] = await Promise.all([
        api.getUsers(),
        api.getAdminDashboard(),
        api.getPendingPayments(),
        api.getInstructorsSubscriptions()
      ]);

      const usersList = usersRes.data.data || usersRes.data;
      const statsData = statsRes.data.data || statsRes.data;
      const pendingList = pendingPaymentsRes.data;
      const subsList = subsRes.data;

      setUsers(Array.isArray(usersList) ? usersList : []);
      setStats(statsData);
      setPendingPayments(Array.isArray(pendingList) ? pendingList : []);
      setSubscriptions(Array.isArray(subsList) ? subsList : []);
    } catch (err: any) {
      console.error('Error fetching admin dashboard data:', err);
      setError('An error occurred while loading data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprovePayment = async (paymentId: number) => {
    const isConfirmed = await confirm({
      title: 'Approve Bank Transfer',
      message: 'Are you sure you have verified this transfer in your HSBC UK bank account and want to approve it? This will immediately activate the user\'s subscription.',
      severity: 'info'
    });
    if (!isConfirmed) return;

    try {
      setApprovingPaymentId(paymentId);
      await api.approvePayment(paymentId);
      showToast('Payment approved and subscription activated successfully!', 'success');
      await fetchData();
    } catch (err) {
      console.error('Error approving payment:', err);
      showToast('An error occurred while approving the payment.', 'error');
    } finally {
      setApprovingPaymentId(null);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    const isConfirmed = await confirm({
      title: currentStatus ? 'Deactivate User' : 'Activate User',
      message: `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`,
      severity: 'warning'
    });
    if (!isConfirmed) return;

    try {
      setUpdatingUserId(userId);
      if (currentStatus) {
        await api.deactivateUser(userId);
      } else {
        await api.activateUser(userId);
      }
      
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === userId ? { ...u, isActive: !currentStatus } : u))
      );
      
      showToast('User status updated successfully.', 'success');
      
      if (stats) {
        setStats({
          ...stats,
          activeUsers: currentStatus ? stats.activeUsers - 1 : stats.activeUsers + 1,
          inactiveUsers: currentStatus ? stats.inactiveUsers + 1 : stats.inactiveUsers - 1
        });
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
      showToast('An error occurred while updating user status.', 'error');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleOpenEditDialog = (sub: InstructorSubscription) => {
    setEditingSub(sub);
    setSubType(sub.subscriptionType);
    setSubExpiryDate(sub.expiryDate ? sub.expiryDate.split('T')[0] : '');
    setHasWhatsApp(sub.hasWhatsAppAddon);
    setSubNotes('');
    setDialogOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!editingSub) return;

    try {
      setSavingSub(true);
      await api.updateInstructorSubscription({
        userId: editingSub.userId,
        subscriptionType: subType,
        expiryDate: subExpiryDate ? new Date(subExpiryDate).toISOString() : undefined,
        hasWhatsAppAddon: hasWhatsApp,
        notes: subNotes || `Adjusted by administrator`
      });

      showToast('Subscription updated successfully!', 'success');
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving subscription:', err);
      showToast('An error occurred while updating the subscription.', 'error');
    } finally {
      setSavingSub(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const query = searchTerm.toLowerCase();
    return fullName.includes(query) || email.includes(query) || user.userType.toLowerCase().includes(query);
  });

  const filteredSubscriptions = subscriptions.filter(sub => {
    const fullName = `${sub.firstName} ${sub.lastName}`.toLowerCase();
    const email = sub.email.toLowerCase();
    const query = searchTerm.toLowerCase();
    return fullName.includes(query) || email.includes(query) || sub.subscriptionType.toLowerCase().includes(query) || sub.subscriptionStatus.toLowerCase().includes(query);
  });

  // Client-calculated summary statistics
  const activePremiumCount = subscriptions.filter(s => s.subscriptionStatus === 'Active' && (s.subscriptionType === 'Monthly' || s.subscriptionType === 'Annual')).length;
  const activeTrialCount = subscriptions.filter(s => s.subscriptionStatus === 'Active' && s.subscriptionType === 'Trial').length;
  const totalRevenue = subscriptions.reduce((sum, sub) => {
    const subSum = (sub.paymentHistory || [])
      .filter(p => p.paymentStatus === 'Completed')
      .reduce((s, p) => s + p.amount, 0);
    return sum + subSum;
  }, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        Admin Management Panel
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Users</Typography>
                  <Typography variant="h3" fontWeight="bold">{stats.totalUsers}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Instructors</Typography>
                  <Typography variant="h3" fontWeight="bold">{stats.totalInstructors}</Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Students</Typography>
                  <Typography variant="h3" fontWeight="bold">{stats.totalStudents}</Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: '#7b1fa2', color: '#fff', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Active Premium Plans</Typography>
                  <Typography variant="h3" fontWeight="bold">{activePremiumCount}</Typography>
                </Box>
                <StarIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: '#e65100', color: '#fff', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Active Free Trials</Typography>
                  <Typography variant="h3" fontWeight="bold">{activeTrialCount}</Typography>
                </Box>
                <StarIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: '#2e7d32', color: '#fff', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Revenue</Typography>
                  <Typography variant="h3" fontWeight="bold">£{totalRevenue.toFixed(2)}</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold" sx={{ opacity: 0.5 }}>£</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Pending Bank Transfers Table */}
      {pendingPayments.length > 0 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', mb: 4, border: '1px solid rgba(46, 125, 50, 0.3)', bgcolor: 'rgba(46, 125, 50, 0.01)' }}>
          <Typography variant="h6" fontWeight="bold" color="success.main" mb={2}>
            Pending Bank Transfers ({pendingPayments.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Transaction ID</b></TableCell>
                  <TableCell><b>Instructor ID</b></TableCell>
                  <TableCell><b>Amount</b></TableCell>
                  <TableCell><b>Reference / Notes</b></TableCell>
                  <TableCell><b>Date Submitted</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingPayments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell><code>{payment.transactionId}</code></TableCell>
                    <TableCell>{payment.instructorId}</TableCell>
                    <TableCell><b>£{payment.amount.toFixed(2)}</b></TableCell>
                    <TableCell>{payment.notes || 'N/A'}</TableCell>
                    <TableCell>{payment.paymentDate ? new Date(payment.paymentDate).toLocaleString() : 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleApprovePayment(payment.id)}
                        disabled={approvingPaymentId === payment.id}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {approvingPaymentId === payment.id ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Approve'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tabs Menu */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => { setActiveTab(newValue); setSearchTerm(''); }} aria-label="admin tabs">
          <Tab label="User Accounts" id="tab-0" />
          <Tab label="Instructors & Subscriptions" id="tab-1" />
          <Tab label="Payment & Transaction History" id="tab-2" />
        </Tabs>
      </Box>

      {/* TAB 0: User Accounts list */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h6" fontWeight="medium">
              User Accounts ({filteredUsers.length} Users)
            </Typography>
            <TextField
              size="small"
              placeholder="Search by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>Role</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.firstName} {user.lastName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.userType === 'Admin' ? 'Admin' : user.userType === 'Instructor' ? 'Instructor' : 'Student'}
                          color={user.userType === 'Admin' ? 'secondary' : user.userType === 'Instructor' ? 'success' : 'primary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={user.isActive ? 'outlined' : 'contained'}
                          color={user.isActive ? 'error' : 'success'}
                          onClick={() => handleToggleStatus(user.id, user.isActive)}
                          disabled={updatingUserId === user.id}
                          startIcon={user.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        >
                          {updatingUserId === user.id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : user.isActive ? (
                            'Deactivate'
                          ) : (
                            'Activate'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* TAB 1: Instructors & Subscriptions list */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h6" fontWeight="medium">
              Instructor Subscriptions ({filteredSubscriptions.length} Instructors)
            </Typography>
            <TextField
              size="small"
              placeholder="Search by name, email, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Instructor</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Plan Type</b></TableCell>
                  <TableCell><b>Expiry Date</b></TableCell>
                  <TableCell><b>WhatsApp Addon</b></TableCell>
                  <TableCell><b>Last Payment</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No instructors found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscriptions.map((sub) => (
                    <TableRow key={sub.instructorId} hover>
                      <TableCell><b>{sub.firstName} {sub.lastName}</b></TableCell>
                      <TableCell>{sub.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={sub.subscriptionStatus}
                          color={sub.subscriptionStatus === 'Active' ? 'success' : sub.subscriptionStatus === 'Expired' ? 'error' : 'default'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.subscriptionType}
                          color={sub.subscriptionType === 'Trial' ? 'info' : sub.subscriptionType === 'Annual' ? 'secondary' : sub.subscriptionType === 'Monthly' ? 'primary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.hasWhatsAppAddon ? 'Enabled' : 'Disabled'}
                          color={sub.hasWhatsAppAddon ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {sub.lastPaymentAmount !== null && sub.lastPaymentAmount !== undefined ? (
                          <Typography variant="body2">
                            <b>£{sub.lastPaymentAmount.toFixed(2)}</b>
                            <br />
                            <small style={{ color: 'gray' }}>
                              {sub.lastPaymentDate ? new Date(sub.lastPaymentDate).toLocaleDateString() : ''} ({sub.lastPaymentMethod})
                            </small>
                          </Typography>
                        ) : (
                          'No Payment'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEditDialog(sub)}
                          sx={{ fontWeight: 'bold' }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* TAB 2: Payment & Transaction History */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h6" fontWeight="medium">
              System Payments & Transaction History
            </Typography>
            <TextField
              size="small"
              placeholder="Search by instructor, email, or transaction ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 350 } }}
            />
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Instructor</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>Transaction ID</b></TableCell>
                  <TableCell><b>Method</b></TableCell>
                  <TableCell><b>Amount</b></TableCell>
                  <TableCell><b>Expiry Date</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Notes</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const allPayments = subscriptions.flatMap(sub => 
                    (sub.paymentHistory || []).map(p => ({
                      ...p,
                      instructorName: `${sub.firstName} ${sub.lastName}`,
                      instructorEmail: sub.email
                    }))
                  ).sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());

                  const filteredPayments = allPayments.filter(p => {
                    const query = searchTerm.toLowerCase();
                    return (
                      p.instructorName.toLowerCase().includes(query) ||
                      p.instructorEmail.toLowerCase().includes(query) ||
                      (p.transactionId && p.transactionId.toLowerCase().includes(query)) ||
                      p.paymentMethod.toLowerCase().includes(query) ||
                      (p.notes && p.notes.toLowerCase().includes(query))
                    );
                  });

                  if (filteredPayments.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>
                            No transactions found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return filteredPayments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>{new Date(payment.paymentDate).toLocaleDateString()} {new Date(payment.paymentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell><b>{payment.instructorName}</b></TableCell>
                      <TableCell>{payment.instructorEmail}</TableCell>
                      <TableCell><code>{payment.transactionId || 'N/A'}</code></TableCell>
                      <TableCell>
                        <Chip
                          label={payment.paymentMethod}
                          size="small"
                          variant="outlined"
                          color={payment.paymentMethod === 'FreeTrial' ? 'info' : payment.paymentMethod === 'BankTransfer' ? 'warning' : 'primary'}
                        />
                      </TableCell>
                      <TableCell><b>£{payment.amount.toFixed(2)}</b></TableCell>
                      <TableCell>
                        {payment.expiryDate ? new Date(payment.expiryDate).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.paymentStatus}
                          color={payment.paymentStatus === 'Completed' ? 'success' : payment.paymentStatus === 'Pending' ? 'warning' : 'error'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {payment.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Subscription Dialog */}
      <Dialog open={dialogOpen} onClose={() => !savingSub && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Edit Subscription for {editingSub?.firstName} {editingSub?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel id="plan-type-label">Subscription Plan Type</InputLabel>
                <Select
                  labelId="plan-type-label"
                  value={subType}
                  label="Subscription Plan Type"
                  onChange={(e) => {
                    setSubType(e.target.value);
                    // Autofill date when plan shifts
                    if (e.target.value === 'Monthly') {
                      setSubExpiryDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    } else if (e.target.value === 'Annual') {
                      setSubExpiryDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    } else if (e.target.value === 'Trial') {
                      setSubExpiryDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    } else {
                      setSubExpiryDate('');
                    }
                  }}
                >
                  <MenuItem value="None">None (No Active Subscription)</MenuItem>
                  <MenuItem value="Trial">Trial (Free / 1-Month Trial)</MenuItem>
                  <MenuItem value="Monthly">Monthly Plan (Base: £10.00 / Premium: £15.00)</MenuItem>
                  <MenuItem value="Annual">Annual Plan (Base: £99.00 / Premium: £149.00)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {subType !== 'None' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Expiration Date"
                  type="date"
                  value={subExpiryDate}
                  onChange={(e) => setSubExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <Box display="flex" gap={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      let baseDate = new Date();
                      if (subExpiryDate) {
                        const parsed = new Date(subExpiryDate);
                        if (!isNaN(parsed.getTime()) && parsed > baseDate) {
                          baseDate = parsed;
                        }
                      }
                      baseDate.setDate(baseDate.getDate() + 30);
                      setSubExpiryDate(baseDate.toISOString().split('T')[0]);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    +1 Month
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      let baseDate = new Date();
                      if (subExpiryDate) {
                        const parsed = new Date(subExpiryDate);
                        if (!isNaN(parsed.getTime()) && parsed > baseDate) {
                          baseDate = parsed;
                        }
                      }
                      baseDate.setFullYear(baseDate.getFullYear() + 1);
                      setSubExpiryDate(baseDate.toISOString().split('T')[0]);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    +1 Year
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setSubExpiryDate(new Date().toISOString().split('T')[0]);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 'bold', ml: 'auto' }}
                  >
                    Reset to Today
                  </Button>
                </Box>
              </Grid>
            )}

            {subType !== 'None' && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasWhatsApp}
                      onChange={(e) => setHasWhatsApp(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <b>Enable WhatsApp Messaging Module</b>
                      <Chip label="+£5/mo or +£50/yr" size="small" color="secondary" variant="outlined" />
                    </Box>
                  }
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Adjustment Reason / Notes"
                placeholder="e.g., Extended free trial for marketing, manual cash payment received, etc."
                multiline
                rows={3}
                value={subNotes}
                onChange={(e) => setSubNotes(e.target.value)}
              />
            </Grid>

            {editingSub?.paymentHistory && editingSub.paymentHistory.length > 0 && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1a237e' }}>
                  Payment History ({editingSub.paymentHistory.length} Transactions)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Date</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Method</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Amount</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Expiry</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editingSub.paymentHistory.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                            {new Date(p.paymentDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px' }}>{p.paymentMethod}</TableCell>
                          <TableCell sx={{ fontSize: '11px', fontWeight: 'bold' }}>
                            £{p.amount.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                            {p.expiryDate ? new Date(p.expiryDate).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px' }}>
                            <Chip 
                              label={p.paymentStatus} 
                              size="small" 
                              color={p.paymentStatus === 'Completed' ? 'success' : p.paymentStatus === 'Pending' ? 'warning' : 'error'}
                              sx={{ fontSize: '9px', height: '18px' }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', color: 'gray', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={savingSub} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveSubscription}
            disabled={savingSub || (subType !== 'None' && !subExpiryDate)}
            variant="contained"
            color="success"
            sx={{ fontWeight: 'bold' }}
          >
            {savingSub ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
